import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  updateFormData,
  calculateInterest,
} from "../redux/actions/investmentForm";
import {
  selectFormData,
  selectCurrency,
  selectCalculatedInterest,
} from "../redux/selectors/investmentFormSelectors";
import useInvestmentProductRate from "./useInvestmentProductRate";

/**
 * Custom hook to handle investment duration terms logic
 */
export const useInvestmentDurationTerms = (
  form: any,
  tenantId?: number,
  investRateId?: number,
  data?: any,
) => {
  const dispatch = useDispatch();

  // Get data from Redux
  const formData = useSelector(selectFormData);
  const calculatedInterest = useSelector(selectCalculatedInterest);
  const currencyCode = useSelector(selectCurrency) || "NGN";

  // Initialize form with values from redux when component mounts

  console.log("formData: ", formData);
  useEffect(() => {
    if (formData) {
      form.setFieldsValue({
        tenure: formData.tenure,
        interest_rate: formData.interest_rate,
        interest_repayment_type:
          data?.investment_product_details[0]?.investment_interest_payment_type
            ?.id,
        number_of_days_in_year: formData.number_of_days_in_year || 365,
      });
    }
  }, [form, formData]);

  // Calculate interest on mount
  useEffect(() => {
    dispatch(calculateInterest());
  }, [dispatch]);

  // Handle tenure change
  const handleTenureChange = (value: number | null) => {
    if (value === null || value === undefined) return;

    // Ensure we have a number
    const numValue = Number(value);

    // Update Redux state
    dispatch(
      updateFormData({
        tenure: numValue,
        interest_repayment_type:
          data?.investment_product_details[0]?.investment_interest_payment_type
            ?.id,
      }),
    );

    // Set form value
    form.setFieldsValue({ tenure: numValue });
  };

  // Handle days in year change
  const handleDaysInYearChange = (value: number) => {
    dispatch(updateFormData({ number_of_days_in_year: value }));
    form.setFieldsValue({ number_of_days_in_year: value });
  };

  // Handle interest repayment type change
  const handleInterestRepaymentTypeChange = (e: any) => {
    const value = e.target.value;
    dispatch(updateFormData({ interest_repayment_type: value }));
    dispatch(calculateInterest());
  };

  // Get current tenure from Redux to ensure we use the most up-to-date value
  const currentTenure = formData.tenure || form.getFieldValue("tenure") || 0;
  const currentPrincipalAmount =
    formData.principal_amount || form.getFieldValue("principal_amount") || 0;

  // Get investment product rate with loading state
  const {
    data: investmentProductRate,
    isLoading: isLoadingInterestRate,
    isFetching: isFetchingInterestRate,
  } = useInvestmentProductRate({
    investRateId: investRateId || 0,
    enteredPrincipalAmount: currentPrincipalAmount,
    tenure: currentTenure,
    tenantId: tenantId || 0,
    depositType:
      data?.investment_product_details[0]?.deposit_type === "RecurringDeposit"
        ? 4
        : 3,
  });

  // Set interest rate from API and recalculate interest when rate changes
  useEffect(() => {
    if (investmentProductRate?.data) {
      const interestRate =
        investmentProductRate?.data?.depositProductInterestRate?.interest;

      // Only update if the interest rate has actually changed
      const currentInterestRate = form.getFieldValue("interest_rate");
      if (currentInterestRate !== interestRate) {
        form.setFieldsValue({ interest_rate: interestRate });
        dispatch(updateFormData({ interest_rate: interestRate }));

        // Calculate interest after a short delay to ensure all form data is updated
        setTimeout(() => {
          dispatch(calculateInterest());
        }, 100);
      }
    }
  }, [investmentProductRate, form, dispatch]);

  return {
    calculatedInterest,
    currencyCode,
    handleTenureChange,
    handleDaysInYearChange,
    handleInterestRepaymentTypeChange,
    hasCalculatedInterest: !!calculatedInterest.interest_calculated,
    isLoadingInterestRate,
    isFetchingInterestRate,
    isCalculating: isLoadingInterestRate || isFetchingInterestRate,
  };
};

export default useInvestmentDurationTerms;
